import React, { useState, useRef, useEffect } from 'react';

import video0 from "../../assets/Lite-view-change-media.mp4";
import video1 from "../../assets/change text.mp4";
import video2 from "../../assets/controls.mp4";
import video3 from "../../assets/showless-edit.mp4";

const cardData = [
  {
    id: 0,
    label: "Media fields",
    description: "Transform your content instantly, swap images and videos, search new media, or use your own uploads in seconds",
    video: video0,
  },
  {
    id: 1,
    label: "Show more",
    description: "Unlock more dynamic properties in your project, edit text and more with ease",
    video: video1,
  },
  {
    id: 2,
    label: "Controls",
    description: "Fine-tune your dynamic layers with intuitive controls, loop seamlessly or pause at the perfect moment",
    video: video2,
  },
  {
    id: 3,
    label: "Show less",
    description: "Focus on what matters, work in a simplified component state and make seamless edits from a single control point",
    video: video3,
  },
];

const CARD_BG = "#F2F3EE";
const PEEK_OFFSET = 20;
const FALLBACK_DURATION = 3500; // ms — used if metadata hasn't loaded yet

// ─── Individual card video — only plays when active ──
const CardVideo = ({ src, isActive, onDurationKnown }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    if (isActive) {
      ref.current.play().catch(() => { });
    } else {
      ref.current.pause();
      ref.current.currentTime = 0;
    }
  }, [isActive]);

  const handleLoadedMetadata = () => {
    if (ref.current && onDurationKnown) {
      onDurationKnown(ref.current.duration * 1000); // convert to ms
    }
  };

  return (
    <video
      ref={ref}
      src={src}
      loop
      playsInline
      muted
      onLoadedMetadata={handleLoadedMetadata}
      className="w-full h-full object-cover block"
    />
  );
};

const StackedCards = ({ isActive, onCardAnimating, onCycleComplete }) => {
  const [activeCard, setActiveCard] = useState(0);
  const cycleCountRef = useRef(0);
  const autoPlayRef = useRef(null);
  const userInteractingRef = useRef(false);
  const interactionTimeoutRef = useRef(null);
  // Store each card's video duration in ms, keyed by card id
  const durationsRef = useRef({});

  const getDuration = (cardId) => durationsRef.current[cardId] ?? FALLBACK_DURATION;

  const stopAutoPlay = () => {
    clearInterval(autoPlayRef.current);
    clearTimeout(autoPlayRef.current);
    autoPlayRef.current = null;
  };

  // Schedule the next card advance after the current card's video duration
  const scheduleNext = (currentCardId, fromManual = false) => {
    stopAutoPlay();
    if (fromManual) cycleCountRef.current = 0;

    const delay = getDuration(currentCardId);

    autoPlayRef.current = setTimeout(() => {
      setActiveCard((prev) => {
        const next = (prev + 1) % cardData.length;
        cycleCountRef.current += 1;

        const isLastCard = next === cardData.length - 1;
        const hasCompletedCycle = cycleCountRef.current >= cardData.length - 1;

        if (isLastCard && hasCompletedCycle && !userInteractingRef.current) {
          // Show last card for its own duration, then hand off to parent
          autoPlayRef.current = setTimeout(() => {
            if (!userInteractingRef.current) {
              onCycleComplete?.();
            }
          }, getDuration(next));
        } else {
          // Schedule the card after this one
          scheduleNext(next);
        }

        return next;
      });
    }, delay);
  };

  // ── Only run when slide 2 becomes active ──
  useEffect(() => {
    if (isActive) {
      setActiveCard(0);
      cycleCountRef.current = 0;
      userInteractingRef.current = false;
      onCardAnimating?.(true);
      scheduleNext(0);
    } else {
      stopAutoPlay();
      clearTimeout(interactionTimeoutRef.current);
      userInteractingRef.current = false;
      onCardAnimating?.(false);
    }

    return () => stopAutoPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const goToCard = (index) => {
    if (index === activeCard) return;

    userInteractingRef.current = true;
    clearTimeout(interactionTimeoutRef.current);
    interactionTimeoutRef.current = setTimeout(() => {
      userInteractingRef.current = false;
    }, 10_000);

    setActiveCard(index);
    scheduleNext(index, true);
  };

  const getCardStyle = (cardIndex) => {
    const total = cardData.length;
    const behind = (cardIndex - activeCard + total) % total;
    return {
      zIndex: total - behind,
      transform: `translateY(${-behind * PEEK_OFFSET}px) scale(${1 - behind * 0.03})`,
      filter: `brightness(${1 - behind * 0.1})`,
      transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.35s ease',
    };
  };

  return (
    <div className="w-full flex flex-col items-center">
      <style>
        {`
          @keyframes dotProgress {
           from { width: 0%; }
           to   { width: 100%; }
          }
        `}
      </style>
      {/* ── Stack area ── */}
      <div
        className="relative w-full"
        style={{ paddingTop: `${(cardData.length - 1) * PEEK_OFFSET}px` }}
      >
        {[...cardData].reverse().map((card) => {
          const cardIsActive = card.id === activeCard;
          return (
            <div
              key={card.id}
              onClick={() => !cardIsActive && goToCard(card.id)}
              className="absolute inset-x-0 bottom-0 rounded-[24px] shadow-lg overflow-hidden"
              style={{
                ...getCardStyle(card.id),
                backgroundColor: CARD_BG,
                cursor: cardIsActive ? 'default' : 'pointer',
              }}
            >
              <div className="p-5 sm:p-6 md:p-8">
                {/* Fixed aspect ratio container so the video never gets clipped */}
                <div className="w-full aspect-video rounded-xl overflow-hidden shadow-[0_10px_30px_-8px_rgba(0,0,0,0.25)] bg-black">
                  <CardVideo
                    src={card.video}
                    isActive={cardIsActive}
                    onDurationKnown={(ms) => { durationsRef.current[card.id] = ms; }}
                  />
                </div>
                <div className="mt-5">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800">
                    {card.label}
                  </h3>
                  <p className="text-sm md:text-base mt-1 font-medium italic text-gray-500">
                    {card.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}

        {/* Invisible spacer */}
        <div className="invisible pointer-events-none rounded-[24px] p-5 sm:p-6 md:p-8">
          <div className="w-full aspect-video rounded-xl" />
          <div className="mt-5">
            <h3 className="text-xl md:text-2xl font-bold">placeholder</h3>
            <p className="text-sm md:text-base mt-1">placeholder description</p>
          </div>
        </div>
      </div>

      {/* ── Dot indicators ── */}
      <div className="flex items-center gap-2 mt-8">
        {cardData.map((card, i) => {
          const isActiveDot = i === activeCard;
          return (
            <button
              key={i}
              onClick={() => goToCard(i)}
              className="relative rounded-full overflow-hidden transition-all duration-300"
              style={{
                width: isActiveDot ? '60px' : '8px',
                height: '8px',
                backgroundColor: '#d1d5db',
                flexShrink: 0,
              }}
            >
              {isActiveDot && (
                <span
                  key={`${activeCard}-progress`}
                  className="absolute inset-y-0 left-0 rounded-full bg-gray-800"
                  style={{
                    width: '100%',
                    animation: `dotProgress ${durationsRef.current[card.id] ?? FALLBACK_DURATION}ms linear forwards`,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

    </div>
  );
};

export default StackedCards;